import {Board, Column, IsEditing, Task} from '../models/types';

import Database, {Metadata, SyncOperation} from './database';
import {StoreName} from "./StoreName";
import {cast} from "../utils/cast";

class BoardStorage {
	private db: Database;

	constructor() {
		this.db = Database.getInstance();
	}

	// Получение доски с колонками и задачами
	async getBoard(boardId: string): Promise<Board | null> {
		const db = await this.db.init();

		try {
			// Получаем доску
			const board :Board|undefined = await db.get(StoreName.Boards, boardId) as Board;
			if (!board) {
				return null;
			}

			// Получаем колонки этой доски
			const columns = await db.getAllFromIndex(StoreName.Columns, 'by-board', boardId);

			// Получаем задачи для каждой колонки
			const tasks: Task[] = [];
			for (const column of columns) {
				let columnTasks: Task[] = cast(await db.getAllFromIndex(StoreName.Tasks, 'by-column', column.id));
				columnTasks = columnTasks.filter((t => !t.deletedAt));
				columnTasks.forEach((task) =>{
					task.boardId = board.id;
				})
				tasks.push(...columnTasks);
			}

			// Сортируем колонки по order
			columns.sort((a, b) => a.order - b.order);

			return cast({
				...board,
				columns,
				tasks,
			});
		} catch (error) {
			console.error('Error getting board:', error);
			return null;
		}
	}

	// Получение всех досок (для списка)
	async getAllBoards(): Promise<Board[]> {
		const db = await this.db.init();

		try {
			const boards = await db.getAll(StoreName.Boards);
			return cast(boards);
		} catch (error) {
			console.error('Error getting all boards:', error);
			return [];
		}
	}

	// Сохранение доски
	async saveBoard(board: Board): Promise<Board> {
		const db = await this.db.init();
		const transaction = db.transaction([StoreName.Boards, StoreName.Columns, StoreName.Tasks], 'readwrite');

		(board as IsEditing).isEditing = false;
		try {
			const now = new Date();
			const boardToSave:Partial<Board> = {
				...board,
				updatedAt: now,
				columns: undefined,
				tasks:undefined
			};

			// Сохраняем доску
			await transaction.objectStore(StoreName.Boards).put(cast(boardToSave));

			// Сохраняем колонки
			for (const column of board.columns) {
				const columnToSave = {
					...column,
					boardId: board.id,
					updatedAt: now,
				};
				await transaction.objectStore(StoreName.Columns).put(columnToSave);
			}

			// Сохраняем задачи
			for (const task of board.tasks) {
				const taskToSave = {
					...task,
					updatedAt: now,
				};
				await transaction.objectStore(StoreName.Tasks).put(taskToSave);
			}

			await transaction.done;

			// Добавляем в очередь синхронизации
			await this.addToSyncQueue('board', board.id, 'update', boardToSave);

			return cast(boardToSave);
		} catch (error) {
			console.error('Error saving board:', error);
			throw error;
		}
	}

	// Создание новой доски
	async createBoard(boardData: Omit<Board, 'id' | 'createdAt' | 'updatedAt'>): Promise<Board> {
		const db = await this.db.init();
		const now = new Date();

		const newBoard: Board = {
			...boardData,
			id: `board_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
			createdAt: now,
			updatedAt: now,
		};

		try {
			await db.add(StoreName.Boards, newBoard);

			// Добавляем в очередь синхронизации
			await this.addToSyncQueue('board', newBoard.id, 'create', newBoard);

			return newBoard;
		} catch (error) {
			console.error('Error creating board:', error);
			throw error;
		}
	}

	// Удаление доски
	async deleteBoard(boardId: string): Promise<void> {
		const db = await this.db.init();
		const transaction = db.transaction([StoreName.Boards, StoreName.Columns, StoreName.Tasks], 'readwrite');

		try {
			// Получаем колонки доски
			const columns = await db.getAllFromIndex(StoreName.Columns, 'by-board', boardId);
			const columnIds = columns.map(col => col.id);

			// Удаляем задачи этих колонок
			for (const columnId of columnIds) {
				const tasks = await db.getAllFromIndex(StoreName.Tasks, 'by-column', columnId) as Task[];
				for (const task of tasks) {
					await transaction.objectStore(StoreName.Tasks).delete(task.id);

					// Добавляем в очередь синхронизации
					await this.addToSyncQueue('task', task.id, 'delete', task);
				}
			}

			// Удаляем колонки
			for (const column of columns) {
				await transaction.objectStore(StoreName.Columns).delete(column.id);

				// Добавляем в очередь синхронизации
				await this.addToSyncQueue('column', column.id, 'delete', column);
			}

			// Удаляем доску
			await transaction.objectStore(StoreName.Boards).delete(boardId);

			// Добавляем в очередь синхронизации
			await this.addToSyncQueue('board', boardId, 'delete', {id: boardId});

			await transaction.done;
		} catch (error) {
			console.error('Error deleting board:', error);
			throw error;
		}
	}

	// Вспомогательный метод для добавления в очередь синхронизации
	private async addToSyncQueue(
		entityType: 'board' | 'column' | 'task',
		entityId: string,
		operation: 'create' | 'update' | 'delete',
		data: any
	): Promise<void> {
		const db = await this.db.init();

		const syncOp: SyncOperation = {
			id: `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
			entityType,
			entityId,
			operation,
			data,
			priority: operation === 'delete' ? 1 : 2, // Удаление - высший приоритет
			attempts: 0,
			maxAttempts: 3,
			createdAt: new Date(),
			updatedAt: new Date(),
		};

		await db.add(StoreName.SyncQueue, syncOp);
	}

	// Получение непросинхронизированных операций
	async getUnsyncedOperations(count?: number): Promise<SyncOperation[]> {
		const db = await this.db.init();

		try {
			const index = db.transaction(StoreName.SyncQueue).store.index('by-priority');
			let cursor = await index.openCursor(null, 'next');

			const operations: SyncOperation[] = [];
			while (cursor && (!count || operations.length < count)) {
				operations.push(cursor.value);
				cursor = await cursor.continue();
			}

			return operations;
		} catch (error) {
			console.error('Error getting unsynced operations:', error);
			return [];
		}
	}

	// Пометить операцию как успешно синхронизированную
	async markAsSynced(operationId: string): Promise<void> {
		const db = await this.db.init();

		try {
			await db.delete(StoreName.SyncQueue, operationId);
		} catch (error) {
			console.error('Error marking operation as synced:', error);
			throw error;
		}
	}

	// Обновление операции после неудачной попытки синхронизации
	async updateSyncOperation(operationId: string, errorMessage?: string): Promise<void> {
		const db = await this.db.init();
		const transaction = db.transaction(StoreName.SyncQueue, 'readwrite');

		try {
			const operation = await transaction.store.get(operationId);
			if (!operation) {
				return;
			}

			const updatedOperation: SyncOperation = {
				...operation,
				attempts: operation.attempts + 1,
				error: errorMessage,
				updatedAt: new Date(),
			};

			await transaction.store.put(updatedOperation);
			await transaction.done;
		} catch (error) {
			console.error('Error updating sync operation:', error);
			throw error;
		}
	}

	// Получение метаданных
	async getMetadata(key: string): Promise<any> {
		const db = await this.db.init();

		try {
			const metadata = await db.get(StoreName.Metadata, key);
			return metadata?.value;
		} catch (error) {
			console.error('Error getting metadata:', error);
			return null;
		}
	}

	// Сохранение метаданных
	async setMetadata(key: string, value: any): Promise<void> {
		const db = await this.db.init();
		const now = new Date();

		const metadata: Metadata = {
			key,
			value,
			updatedAt: now,
		};

		try {
			await db.put(StoreName.Metadata, metadata);
		} catch (error) {
			console.error('Error setting metadata:', error);
			throw error;
		}
	}

	// Получение колонки
	async getColumn(columnId: string): Promise<Column | null> {
		const db = await this.db.init();
		try {
			const column = await db.get(StoreName.Columns, columnId);
			return cast(column || null);
		} catch (error) {
			console.error('Error getting column:', error);
			return null;
		}
	}

	// Сохранение колонки
	async saveColumn(column: Column): Promise<Column> {
		const db = await this.db.init();
		const now = new Date();

		const columnToSave = {
			...column,
			updatedAt: now,
		};

		try {
			await db.put(StoreName.Columns, columnToSave);

			// Добавляем в очередь синхронизации
			await this.addToSyncQueue('column', column.id, 'update', columnToSave);

			return columnToSave;
		} catch (error) {
			console.error('Error saving column:', error);
			throw error;
		}
	}

	// Обновление колонки
	async updateColumn(columnId: string, updates: Partial<Column>): Promise<Column | null> {
		const db = await this.db.init();
		const transaction = db.transaction([StoreName.Columns], 'readwrite');
		const store = transaction.objectStore(StoreName.Columns);

		try {
			const column = await store.get(columnId);
			if (!column) {
				return null;
			}

			const updatedColumn: Column = {
				...column,
				...updates,
				updatedAt: new Date(),
			};

			await store.put(updatedColumn);
			await transaction.done;

			// Добавляем в очередь синхронизации
			await this.addToSyncQueue('column', columnId, 'update', updatedColumn);

			return updatedColumn;
		} catch (error) {
			console.error('Error updating column:', error);
			throw error;
		}
	}

	// Удаление колонки
	async deleteColumn(columnId: string): Promise<boolean> {
		const db = await this.db.init();

		try {
			const column = await this.getColumn(columnId);
			if (!column) {
				return false;
			}

			// Удаляем задачи этой колонки
			const tasks = await db.getAllFromIndex(StoreName.Tasks, 'by-column', columnId);
			for (const task of tasks) {
				await db.delete(StoreName.Tasks, task.id);
				await this.addToSyncQueue('task', task.id, 'delete', task);
			}

			// Удаляем колонку
			await db.delete(StoreName.Columns, columnId);

			// Добавляем в очередь синхронизации
			await this.addToSyncQueue('column', columnId, 'delete', column);

			return true;
		} catch (error) {
			console.error('Error deleting column:', error);
			throw error;
		}
	}
}

export default BoardStorage;
